/*
 * Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
 * This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
 * The receipt or possession of this program does not convey any rights to use, reproduce or disclose
 * its contents in whole or in part, without the specific written consent of Inovalon, Inc.
 * Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
 * is a violation of the copyright laws and may subject you to criminal prosecution.
 */
import { observable, action, computed } from 'mobx'
import { injectable } from 'inversify'
import { injectProperty } from '../../arc/configuration/ServiceLocator'
import { IImportBatchUIApiClient } from '../ImportBatchUIApiClient/contracts'
import { IImportBatchUIStateManager, ICoverageDiscoveryBatchRequest, ICoverageDiscoveryBatchImportGridResults, IBatchExcelUploadResponse, IContinueBatchUpload, IbatchDetail } from './contracts'
import { ValidationResult } from '../../arc/utils/ValidationResult/ValidationResult'
import { isEmpty, isLength } from 'validator'
import { SettingsManager } from '../../configuration/SettingsManager'

@injectable()
export class ImportBatchUIStateManager implements IImportBatchUIStateManager {
	@observable
	coverageDiscoveryBatchRequest: ICoverageDiscoveryBatchRequest

	@observable
	coverageDiscoveryBatchResponseGridErrorResultList: ICoverageDiscoveryBatchImportGridResults

	@observable
	resultMessage: string

	@observable
	rowErrorsCount: number

	@observable
	rowSuccessCount: number

	@observable
	batchDetail: IbatchDetail

	@observable
	erroroneousRowsForExport: any[]

	@observable
	successRowsForExport: any[]

	@observable
	customField: string

	@observable
	isServiceDatePresent: boolean

	@observable
	isRequestRaisedForImport: boolean

	@observable
	coverageDiscoveryImportBatchUIResponse: IBatchExcelUploadResponse

	@injectProperty('IImportBatchUIApiClient')
	public ImportBatchUIApiClient!: IImportBatchUIApiClient

	constructor() {
		this.batchDetail = {
			fileName: '',
			batchName: '',
			metaDataHeaders: [],
		}
		this.coverageDiscoveryBatchRequest = this.getBlankItem()
		this.coverageDiscoveryBatchResponseGridErrorResultList = {
			errorCount: 0,
			coverageDiscoveryBatchResponseGridErrorResultCollection: [],
		}
		this.resultMessage = ''
		this.rowErrorsCount = 0
		this.rowSuccessCount = 0
		this.coverageDiscoveryImportBatchUIResponse = {
			erroroneousRowsCount: 0,
			successfulRowsCount: 0,
			batchName: '',
			fileName: '',
			erroroneousRows: [],
			successfulRows: [],
			metaDataHeaders: [],
			coverageDiscoveryBatchResponseGridErrorResultList: this.coverageDiscoveryBatchResponseGridErrorResultList,
		}
		this.successRowsForExport = []
		this.erroroneousRowsForExport = []
		this.customField = ''
		this.isServiceDatePresent = false
		this.isRequestRaisedForImport = false
	}

	getBlankItem(): ICoverageDiscoveryBatchRequest {
		return {
			batchName: '',
			File: '',
			fileName: '',
		}
	}

	@computed get validateSelectedRequestDetail(): ValidationResult {
		const result = new ValidationResult()
		//const regex = '<("[^"]*"|\'[^\']*\'|[^\'">])*>'
		const regex = new RegExp('[<>\\\\]')

		if (this.coverageDiscoveryBatchRequest !== null && typeof this.coverageDiscoveryBatchRequest !== 'undefined') {
			if (this.coverageDiscoveryBatchRequest.batchName !== null && !isEmpty(this.coverageDiscoveryBatchRequest.batchName)) {
				if (!isLength(this.coverageDiscoveryBatchRequest.batchName, { max: 200 })) {
					result.setMessage('batchName', 'Batch names must be less than 200 characters long.')
				}
				if (this.coverageDiscoveryBatchRequest.batchName.match(regex)) {
					result.setMessage('batchName', 'Batch names must not contain > or < or \\')
				}
			}
			if (this.coverageDiscoveryBatchRequest.fileName !== null && !isEmpty(this.coverageDiscoveryBatchRequest.fileName)) {
				const file = this.coverageDiscoveryBatchRequest.fileName.substring(0, this.coverageDiscoveryBatchRequest.fileName.lastIndexOf('.')) || this.coverageDiscoveryBatchRequest.fileName
				if (!isLength(file, { max: 200 })) {
					result.setMessage('file', 'File names must be less than 200 characters long.')
				}

				if (file.match(regex)) {
					result.setMessage('file', 'File names must not contain > or < or \\')
				}
			}
		}
		return result
	}

	@action setSelectedItem(item: ICoverageDiscoveryBatchRequest): void {
		this.coverageDiscoveryBatchRequest = item
	}

	@action setCoverageDiscoveryErrorGridResults(results: ICoverageDiscoveryBatchImportGridResults): void {
		this.coverageDiscoveryBatchResponseGridErrorResultList = results
	}

	@action sendSucessfulRows(): Promise<void> {
		const continueBatchUploadRequest: IContinueBatchUpload = {
			batchDetail: this.batchDetail,
			successfulRows: this.successRowsForExport.slice(),
		}
		this.isRequestRaisedForImport = true
		return this.ImportBatchUIApiClient.sendSuccessfulRowsData(continueBatchUploadRequest).catch(error => {
			if (error.response !== null && typeof error.response !== 'undefined' && error.response.data.userMessage) {
				throw new Error(error.response.data.userMessage)
			} else {
				throw new Error('An error occurred while uploading the batch')
			}
		})
	}

	@action saveOrUpdateSelectedItem(): Promise<void> {
		this.isRequestRaisedForImport = true
		const batchName = this.coverageDiscoveryBatchRequest.batchName
		this.coverageDiscoveryBatchRequest.batchName = batchName.length === 200 ? batchName.substr(0, 185) : batchName
		return this.ImportBatchUIApiClient.saveItem(this.coverageDiscoveryBatchRequest)
			.catch(error => {
				if (error.response !== null && typeof error.response !== 'undefined' && error.response.data.userMessage) {
					throw new Error(error.response.data.userMessage)
				} else {
					throw new Error('An error occurred while uploading the batch')
				}
			})
			.then(response => {
				if (response) {
					this.rowSuccessCount = response.successfulRowsCount

					if (response.erroroneousRowsCount !== 0) {
						this.rowErrorsCount = response.erroroneousRowsCount
						this.setCoverageDiscoveryErrorGridResults(response.coverageDiscoveryBatchResponseGridErrorResultList)
						response.erroroneousRows.forEach(element => {
							this.erroroneousRowsForExport.push(element)
						})
						response.successfulRows.forEach(element => {
							this.successRowsForExport.push(element)
						})
						this.batchDetail.batchName = response.batchName
						this.batchDetail.fileName = response.fileName
						if (typeof response.metaDataHeaders !== 'undefined') {
							response.metaDataHeaders.forEach(element => {
								this.batchDetail.metaDataHeaders.push(element)
							})
						}
						const contains = string => response.erroroneousRows.findIndex(obj => Object.keys(obj).includes(string))
						if (contains('isDateOfServicePresent') !== -1) {
							response.erroroneousRows.some(element => {
								if (element['isDateOfServicePresent'] === true) return (this.isServiceDatePresent = true)
								else return (this.isServiceDatePresent = false)
							})
						}
						this.resultMessage = ` ${response.successfulRowsCount} request(s) can be imported successfully. ${response.erroroneousRowsCount} request(s) are missing and/or invalid information.`
					}
				}
			})
	}
}
